import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";

const InvoiceRequestForm = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: "",
    isCompany: false,
    address: "",
    country: "",
    vatId: "",
    plan: searchParams.get("plan") || "",
  });

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, type, value } = e.target;
    const checked = type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  
    const payload = {
      userId: user?.id || "",
      email: user?.email || "",
      ...formData,
    };
  
    try {
      const response = await fetch("/api-admin/invoice-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        const errData = await response.json();
        setStatus("error");
        setErrorMessage(errData?.error || "Something went wrong.");
      } else {
        setStatus("success");
      }
    } catch (error) {
      console.error("Invoice submission failed:", error);
      setStatus("error");
      setErrorMessage("Unexpected error occurred.");
    }
  };
  

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-xl mx-auto p-6 bg-white rounded shadow space-y-4"
    >
      <h2 className="text-xl font-bold text-teal-700 mb-4">Request an Invoice</h2>

      {status === "success" && (
        <div className="bg-green-100 text-green-800 p-3 rounded">✅ Request submitted!</div>
      )}

      {status === "error" && (
        <div className="bg-red-100 text-red-800 p-3 rounded">
          ❌ {errorMessage}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium">Full Name or Company Name</label>
        <input
          type="text"
          name="name"
          required
          className="mt-1 block w-full border px-3 py-2 rounded"
          onChange={handleChange}
        />
      </div>

      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            name="isCompany"
            onChange={handleChange}
            checked={formData.isCompany}
          />
          <span>I represent a company</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium">Billing Address</label>
        <input
          type="text"
          name="address"
          required
          className="mt-1 block w-full border px-3 py-2 rounded"
          onChange={handleChange}
        />
      </div>

      <div>
        <label className="block text-sm font-medium">Country</label>
        <input
          type="text"
          name="country"
          required
          className="mt-1 block w-full border px-3 py-2 rounded"
          onChange={handleChange}
        />
      </div>

      {formData.isCompany && (
        <div>
          <label className="block text-sm font-medium">VAT ID (optional)</label>
          <input
            type="text"
            name="vatId"
            className="mt-1 block w-full border px-3 py-2 rounded"
            onChange={handleChange}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium">Plan to Purchase</label>
        <select
          name="plan"
          required
          className="mt-1 block w-full border px-3 py-2 rounded"
          onChange={handleChange}
          value={formData.plan}
        >
          <option value="">Select a plan</option>
          <option value="basic">Basic Subscription – $7.99</option>
          <option value="pro">Pro Subscription – $14.99</option>
          <option value="paygo-small">Pay-as-you-go Small – $7.99</option>
          <option value="paygo-large">Pay-as-you-go Large – $14.99</option>
          <option value="enterprise">Enterprise / Custom</option>
        </select>
      </div>

      <button
        type="submit"
        className="w-full bg-teal-600 text-white py-2 rounded hover:bg-teal-700"
        disabled={status === "success"}
      >
        Submit Invoice Request
      </button>
    </form>
  );
};

export default InvoiceRequestForm;
